"use client";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const baseButtonStyles =
  "flex w-auto flex-row rounded-2xl text-white items-center font-bold text-sm gap-2 justify-center items-center";
const baseButtonAnimations =
  "transition-all duration-400 ease-in-out active:scale-105";
const baseDisabledButtonStyles =
  "disabled:!p-current disabled:!scale-100 disabled:!opacity-50 disabled:cursor-not-allowed";

const buttonVariants = cva(
  cn(baseButtonStyles, baseButtonAnimations, baseDisabledButtonStyles),
  {
    variants: {
      variant: {
        //** NEW VARIANT START **//
        outline: "bg-white border border-assistant text-assistant",
        //** NEW VARIANT END **//

        default:
          "border bg-slate-900 text-slate-50 hover:bg-slate-900/90 dark:bg-slate-50 dark:text-slate-900 dark:hover:bg-slate-50/90",
        gradient: "border bg-gradient hover:bg-gradient-secondary",
        primary: "border border-primary hover:bg-primary/10 text-primary ",
        secondary: "border border-secondary bg-secondary",
        accent: "border border-accent bg-accent",
        "primary-dark": "border bg-primary hover:bg-primary/90 text-white ",
        ghost: "",
        white: "border bg-white text-assistant hover:bg-slate-100",
        assistant:
          "border bg-white border border-assistant text-assistant hover:bg-slate-100 disabled:!p-current disabled:!scale-100 active:scale-[1.015] py-3 active:py-4 active:my-0.5 px-4 m-0 active:!px-3 active:mx-1 !h-6 flex gap-2 !p-2 text-sm disabled:opacity-30 disabled:cursor-not-allowed",
        black: "border bg-white text-black hover:bg-gray-secondary",
        social:
          "border border-primary bg-white rounded-full hover:bg-white text-primary",
        account: "bg-white",
        nav: "text-assistant"
      },
      size: {
        default: "!h-[42px] px-4 py-3 m-0",
        sm: "px-2 py-1",
        md: "px-2 py-3",
        lg: "px-4 py-3",
        icon: "px-0 py-0"
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
